<template>
  <v-container fluid class="px-7 py-6">
    <div class="font-weight-bold grey--text text--darken-1 text-overline">Executar tarefa</div>
    <div class="align-center d-flex mt-1">
      <h2 class="font-weight-medium ma-0 text-h6">Analisar erro</h2>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn aria-label="Como analisar o erro" class="ml-1" color="primary" icon small v-bind="attrs" v-on="on">
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
        </template>
        <span class="d-inline-block" style="max-width: 340px">
          Salve a análise como rascunho quantas vezes precisar. O encaminhamento ao encerramento só ocorre ao concluir.
        </span>
      </v-tooltip>
    </div>

    <v-textarea
      v-model="descricao"
      class="mb-3 mt-5"
      auto-grow
      counter="1000"
      label="Dados da análise"
      maxlength="1000"
      outlined
      rows="7"
    />

    <v-select v-model="origemErro" :items="origensErro" item-text="text" item-value="value" label="Origem do erro" outlined />

    <v-textarea
      v-model="correcaoAplicada"
      auto-grow
      counter="1000"
      label="Correção aplicada"
      maxlength="1000"
      outlined
      rows="7"
    />

    <v-expansion-panels accordion flat>
      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-medium pt-0 px-0">
          Evidências {{ quantidadeEvidenciasPreenchidas ? `(${quantidadeEvidenciasPreenchidas})` : '' }}
        </v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-expansion-panels accordion flat>
            <v-expansion-panel v-for="(justificativa, indice) in justificativas" :key="justificativa.chave">
              <v-expansion-panel-header class="px-0">
                {{ tituloGrupoEvidencias(justificativa) }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <justificativa-analise
                  :key="`${justificativa.chave}-${versaoAnalise}`"
                  v-model="justificativas[indice]"
                  :bdgd="bdgd"
                  :entidade="erro.entidade || ''"
                  :erro-id="erro.id"
                  tipo-fixo
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-alert v-if="erroExecucao" class="mb-0 mt-5" dense outlined type="error">{{ erroExecucao }}</v-alert>

    <div class="d-flex justify-end mt-8">
      <v-btn class="mr-3" :disabled="executando" outlined @click="salvar(false)">Salvar parcialmente</v-btn>
      <botao-confirmar-tarefa label="Encerrar" :disabled="!podeConcluir" :loading="executando" @click="salvar(true)" />
    </div>
  </v-container>
</template>

<script>
  import GestaoBdgdsErrosTarefasService from '@/services/GestaoBdgdsErrosTarefasService.js';
  import { origensErro } from './origensErro.js';
  import BotaoConfirmarTarefa from './BotaoConfirmarTarefa.vue';
  import JustificativaAnalise from './JustificativaAnalise.vue';

  const tiposJustificativa = {
    FOTO: 'Imagem(s)',
    RELATORIO: 'Relatório',
    EXTRACAO_BDGD: 'Extração BDGD',
    CONSULTA_SQL: 'Consulta SQL',
    VALIDACAO_BDGD: 'Validação de BDGD'
  };

  export default {
    name: 'AnalisarErro',

    components: { BotaoConfirmarTarefa, JustificativaAnalise },

    props: {
      bdgd: { type: Object, default: () => ({}) },
      erro: { type: Object, required: true },
      tarefa: { type: Object, required: true }
    },

    data() {
      return {
        origensErro,
        descricao: '',
        correcaoAplicada: '',
        origemErro: null,
        justificativas: [],
        versaoAnalise: 0,
        executando: false,
        erroExecucao: ''
      };
    },

    computed: {
      quantidadeEvidenciasPreenchidas() {
        return this.justificativas.filter(this.justificativaPreenchida).length;
      },

      podeConcluir() {
        return Boolean(
          this.descricao.trim() &&
            this.correcaoAplicada.trim() &&
            this.origemErro &&
            this.quantidadeEvidenciasPreenchidas &&
            !this.executando
        );
      }
    },

    methods: {
      rotuloJustificativa(tipo) {
        return tiposJustificativa[tipo] || 'Nova evidência';
      },

      tituloGrupoEvidencias(justificativa) {
        const quantidade =
          {
            FOTO: justificativa.arquivos?.length,
            RELATORIO: justificativa.relatorios?.length,
            EXTRACAO_BDGD: justificativa.extracoesBdgd?.length,
            VALIDACAO_BDGD: justificativa.validacoesBdgds?.length,
            CONSULTA_SQL: justificativa.consultasSql?.length
          }[justificativa.tipo_justificativa] || 0;

        return `${this.rotuloJustificativa(justificativa.tipo_justificativa)}${quantidade ? ` (${quantidade})` : ''}`;
      },

      justificativaPreenchida(justificativa) {
        if (!justificativa.tipo_justificativa) return false;
        if (justificativa.tipo_justificativa === 'FOTO') return Boolean(justificativa.arquivos?.length);
        if (justificativa.tipo_justificativa === 'RELATORIO') return Boolean(justificativa.relatorios?.length);
        if (justificativa.tipo_justificativa === 'EXTRACAO_BDGD') return Boolean(justificativa.extracoesBdgd?.length);
        if (justificativa.tipo_justificativa === 'VALIDACAO_BDGD') return Boolean(justificativa.validacoesBdgds?.length);

        return Boolean(
          justificativa.consultasSql?.length &&
            justificativa.consultasSql.every((consulta) => {
              if (!consulta.data_consulta) return false;
              if (consulta.modoConsultaSql === 'ARQUIVOS') return Boolean(consulta.arquivos?.length);

              return Boolean(consulta.consulta_sql?.trim());
            })
        );
      },

      agruparJustificativasPorTipo(justificativas) {
        const grupos = new Map();

        justificativas.forEach((justificativa, indice) => {
          const tipo = justificativa.tipo_justificativa;
          const chave = tipo || `nova-${indice}`;
          const grupo = grupos.get(chave) || {
            chave: tipo ? `tipo-${tipo}` : chave,
            tipo_justificativa: tipo || null,
            arquivos: [],
            relatorios: [],
            extracoesBdgd: [],
            validacoesBdgds: [],
            consultasSql: []
          };

          if (tipo === 'FOTO') {
            grupo.arquivos.push(...(justificativa.arquivos || []));
          } else if (tipo === 'RELATORIO' && justificativa.relatorio) {
            grupo.relatorios.push(justificativa.relatorio);
          } else if (tipo === 'EXTRACAO_BDGD' && justificativa.extracao_bdgd) {
            grupo.extracoesBdgd.push(justificativa.extracao_bdgd);
          } else if (tipo === 'VALIDACAO_BDGD' && justificativa.validacao_bdgd) {
            grupo.validacoesBdgds.push(justificativa.validacao_bdgd);
          } else if (tipo === 'CONSULTA_SQL') {
            const arquivos = justificativa.arquivos || [];
            const modoConsultaSql = arquivos.length && !justificativa.consulta_sql ? 'ARQUIVOS' : 'TEXTO';

            grupo.consultasSql.push({
              chave: `consulta-${justificativa.id || indice}`,
              modoConsultaSql,
              consulta_sql: justificativa.consulta_sql || '',
              data_consulta: justificativa.data_consulta ? String(justificativa.data_consulta).slice(0, 10) : null,
              arquivos: modoConsultaSql === 'ARQUIVOS' ? arquivos : []
            });
          }

          grupos.set(chave, grupo);
        });

        return Object.keys(tiposJustificativa).map(
          (tipo) =>
            grupos.get(tipo) || {
              chave: `tipo-${tipo}`,
              tipo_justificativa: tipo
            }
        );
      },

      preencherAnalise(analise = {}) {
        this.descricao = analise.descricao || '';
        this.correcaoAplicada = analise.correcao_aplicada || '';
        this.origemErro = analise.origem_erro || null;
        this.justificativas = this.agruparJustificativasPorTipo(analise.justificativas || []);
        this.versaoAnalise += 1;
      },

      dadosAnalise() {
        return {
          tarefa_id: this.tarefa.id,
          descricao: this.descricao.trim() || null,
          correcao_aplicada: this.correcaoAplicada.trim() || null,
          origem_erro: this.origemErro,
          justificativas: this.justificativas.filter(this.justificativaPreenchida).reduce((justificativas, justificativa) => {
            if (justificativa.tipo_justificativa === 'CONSULTA_SQL') {
              return justificativas.concat(
                (justificativa.consultasSql || []).map((consulta) => {
                  const dados = {
                    tipo_justificativa: justificativa.tipo_justificativa,
                    data_consulta: consulta.data_consulta
                  };

                  if (consulta.modoConsultaSql === 'ARQUIVOS') {
                    dados.arquivo_ids = (consulta.arquivos || []).map((arquivo) => arquivo.id);
                  } else {
                    dados.consulta_sql = consulta.consulta_sql;
                  }

                  return dados;
                })
              );
            }

            const dados = { tipo_justificativa: justificativa.tipo_justificativa };

            if (justificativa.tipo_justificativa === 'FOTO') {
              dados.arquivo_ids = (justificativa.arquivos || []).map((arquivo) => arquivo.id);
            }
            if (justificativa.tipo_justificativa === 'RELATORIO') {
              dados.relatorio_ids = (justificativa.relatorios || []).map((relatorio) => relatorio.id);
            }
            if (justificativa.tipo_justificativa === 'EXTRACAO_BDGD') {
              dados.extracao_bdgd_ids = (justificativa.extracoesBdgd || []).map((extracao) => extracao.id);
            }
            if (justificativa.tipo_justificativa === 'VALIDACAO_BDGD') {
              dados.validacao_bdgd_ids = (justificativa.validacoesBdgds || []).map((validacao) => validacao.id);
            }
            justificativas.push(dados);

            return justificativas;
          }, [])
        };
      },

      async salvar(concluir) {
        if (this.executando || (concluir && !this.podeConcluir)) return;

        this.executando = true;
        this.erroExecucao = '';

        try {
          const executar = concluir
            ? GestaoBdgdsErrosTarefasService.concluirAnalise
            : GestaoBdgdsErrosTarefasService.salvarAnalise;
          await executar(this.erro.id, this.dadosAnalise());

          if (concluir) {
            this.$emit('executada');
          } else {
            this.$toast.success('Análise salva parcialmente.', '', { position: 'topRight' });
          }
        } catch (error) {
          this.erroExecucao = error?.response?.data?.message || 'Não foi possível salvar a análise.';
        } finally {
          this.executando = false;
        }
      }
    },

    watch: {
      erro: {
        immediate: true,
        handler(erro) {
          this.preencherAnalise(erro?.analise || {});
        }
      }
    }
  };
</script>

<style scoped>
  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 !important;
  }
</style>
